import React, { useEffect, useState } from "react";
import { Form, Container, Spinner, Alert } from "react-bootstrap";

const ProductSearch = ({ onSearchResults }) => {
  const [searchType, setSearchType] = useState("name");
  const [name, setName] = useState("");
  const [priceRange, setPriceRange] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  /*
   * SEARCH BY NAME
   *
   * Runs automatically whenever the user types.
   */
  useEffect(() => {
    if (searchType !== "name") return;

    // If the search box is empty, show all active products.
    if (name.trim() === "") {
      fetch(`${API_URL}/products/active`)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Unable to load products.");
          }

          return response.json();
        })
        .then((data) => {
          onSearchResults(data);
        })
        .catch((error) => {
          console.error("Error loading products:", error);
          setError("Unable to load products.");
        });

      return;
    }

    // Small delay so we don't make an API request for every single
    // keystroke immediately.
    const delay = setTimeout(async () => {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_URL}/products/search-by-name`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: name.trim(),
          }),
        });

        if (!response.ok) {
          throw new Error("Unable to search products.");
        }

        const data = await response.json();

        onSearchResults(data);
      } catch (error) {
        console.error("Error searching products:", error);
        setError("Unable to search products.");
      } finally {
        setLoading(false);
      }
    }, 400);

    // IMPORTANT:
    // Cancel the previous timer when the user types again.
    return () => clearTimeout(delay);
  }, [name, searchType, API_URL, onSearchResults]);

  /*
   * SEARCH BY PRICE
   */
  useEffect(() => {
    if (searchType !== "price") return;

    // No price selected = nothing to search.
    if (!priceRange) return;

    const searchByPrice = async () => {
      setLoading(true);
      setError("");

      let minPrice;
      let maxPrice;

      if (priceRange === "0-500") {
        minPrice = 0;
        maxPrice = 500;
      }

      if (priceRange === "501-1000") {
        minPrice = 501;
        maxPrice = 1000;
      }

      if (priceRange === "1000-plus") {
        minPrice = 1000;
        maxPrice = 999999999;
      }

      try {
        const response = await fetch(`${API_URL}/products/search-by-price`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            minPrice,
            maxPrice,
          }),
        });

        if (!response.ok) {
          throw new Error("Unable to search products.");
        }

        const data = await response.json();

        onSearchResults(data);
      } catch (error) {
        console.error("Error searching products:", error);
        setError("Unable to search products.");
      } finally {
        setLoading(false);
      }
    };

    searchByPrice();
  }, [priceRange, searchType, API_URL, onSearchResults]);

  /*
   * SWITCH SEARCH TYPE
   */
  const handleSearchTypeChange = (type) => {
    setSearchType(type);

    // Reset everything when switching tabs.
    setName("");
    setPriceRange("");
    setError("");
    setLoading(false);

    // Show all products again.
    fetch(`${API_URL}/products/active`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load products.");
        }

        return response.json();
      })
      .then((data) => {
        onSearchResults(data);
      })
      .catch((error) => {
        console.error("Error loading products:", error);
        setError("Unable to load products.");
      });
  };

  return (
    <section className="product-search">
      <Container>
        {/* Heading */}
        <div className="product-search-header text-center">
          <span className="section-eyebrow">FIND YOUR FAVORITE</span>

          <h2>Explore our desserts</h2>

          <p>
            Search our collection by name or find something within your budget.
          </p>
        </div>

        {/* Search Box */}
        <div className="product-search-box">
          {/* Search Type Tabs */}
          <div className="search-type">
            <button
              type="button"
              className={`search-type-button ${
                searchType === "name" ? "active" : ""
              }`}
              onClick={() => handleSearchTypeChange("name")}
            >
              Search by name
            </button>

            <button
              type="button"
              className={`search-type-button ${
                searchType === "price" ? "active" : ""
              }`}
              onClick={() => handleSearchTypeChange("price")}
            >
              Search by budget
            </button>
          </div>

          {/* NAME SEARCH */}
          {searchType === "name" && (
            <Form.Group>
              <Form.Label>What are you craving?</Form.Label>

              <Form.Control
                type="text"
                placeholder="e.g. Chocolate Cake"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="off"
              />

              {loading && (
                <div className="mt-2">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Searching...</span>
                </div>
              )}
            </Form.Group>
          )}

          {/* PRICE SEARCH */}
          {searchType === "price" && (
            <div>
              <Form.Label>What's your budget?</Form.Label>

              <div className="price-range-options">
                <button
                  type="button"
                  className={`price-range-button ${
                    priceRange === "0-500" ? "active" : ""
                  }`}
                  onClick={() => setPriceRange("0-500")}
                >
                  <span>₱0–₱500</span>
                  <small>Sweet & affordable</small>
                </button>

                <button
                  type="button"
                  className={`price-range-button ${
                    priceRange === "501-1000" ? "active" : ""
                  }`}
                  onClick={() => setPriceRange("501-1000")}
                >
                  <span>₱501–₱1,000</span>
                  <small>Something special</small>
                </button>

                <button
                  type="button"
                  className={`price-range-button ${
                    priceRange === "1000-plus" ? "active" : ""
                  }`}
                  onClick={() => setPriceRange("1000-plus")}
                >
                  <span>₱1,000+</span>
                  <small>Go all out</small>
                </button>
              </div>

              {loading && (
                <div className="mt-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Finding desserts...</span>
                </div>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert variant="danger" className="product-search-error mt-3">
              {error}
            </Alert>
          )}
        </div>
      </Container>
    </section>
  );
};

export default ProductSearch;
